/**
 * EmailListField — mehrere E-Mail-Adressen pro Kontakt mit Label + Primary-Marker (P1).
 *
 * Datenmodell:
 *  - Legacy: string[] (nur E-Mails, keine Labels)
 *  - Neu:    { email, label, is_primary }[]
 *
 * Ausgabe an das Backend: Mischform ist erlaubt (contact_utils.py normalisiert).
 * Der interne State speichert immer als Object-Array; beim Save wird das
 * Object-Array durchgereicht (backwards-compatible).
 */
import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash, Star } from "@phosphor-icons/react";

function toObj(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((it, idx) => {
      if (typeof it === "string") {
        return { email: it, label: "", is_primary: idx === 0 };
      }
      return {
        email: it?.email || "",
        label: it?.label || "",
        is_primary: !!it?.is_primary,
      };
    })
    .filter((e, idx, arr) => e.email || idx === arr.length - 1); // letzten leeren behalten
}

const COMMON_LABELS = ["privat", "geschäftlich", "Rechnungswesen", "Support", "sonstige"];

export default function EmailListField({ emails, onChange }) {
  const [items, setItems] = useState(() => toObj(emails).length ? toObj(emails) : [{ email: "", label: "", is_primary: true }]);

  // Sync von außen (falls Form-Reset)
  useEffect(() => {
    const norm = toObj(emails);
    if (norm.length === 0) {
      setItems([{ email: "", label: "", is_primary: true }]);
    } else {
      // Sicherstellen dass mind. eine als primary markiert ist
      if (!norm.some((e) => e.is_primary)) norm[0].is_primary = true;
      setItems(norm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = (list) => {
    setItems(list);
    // Nur befüllte Einträge nach oben propagieren
    const clean = list.filter((e) => (e.email || "").trim());
    if (clean.length && !clean.some((e) => e.is_primary)) clean[0].is_primary = true;
    onChange(clean);
  };

  const setItem = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    emit(next);
  };

  const removeItem = (idx) => {
    let next = items.filter((_, i) => i !== idx);
    if (next.length === 0) next = [{ email: "", label: "", is_primary: true }];
    if (!next.some((e) => e.is_primary)) next[0].is_primary = true;
    emit(next);
  };

  const makePrimary = (idx) => {
    const next = items.map((it, i) => ({ ...it, is_primary: i === idx }));
    emit(next);
  };

  const addRow = () => {
    emit([...items, { email: "", label: "", is_primary: false }]);
  };

  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
        E-Mail-Adressen
      </div>
      <div className="space-y-1.5" data-testid="contact-emails-list">
        {items.map((it, idx) => (
          <div key={idx} className="grid grid-cols-[1fr_140px_auto_auto] gap-1.5 items-center">
            <Input
              type="email"
              placeholder="name@firma.de"
              value={it.email}
              onChange={(e) => setItem(idx, { email: e.target.value })}
              data-testid={`contact-email-${idx}`}
              className="h-9"
            />
            <Input
              placeholder="Label (opt.)"
              value={it.label}
              onChange={(e) => setItem(idx, { label: e.target.value })}
              list={`email-label-list-${idx}`}
              data-testid={`contact-email-label-${idx}`}
              className="h-9 text-xs"
            />
            <datalist id={`email-label-list-${idx}`}>
              {COMMON_LABELS.map((l) => <option key={l} value={l} />)}
            </datalist>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => makePrimary(idx)}
              disabled={!it.email}
              title={it.is_primary ? "Primäre Adresse" : "Als primär markieren"}
              data-testid={`contact-email-primary-${idx}`}
              className="h-9 w-9"
            >
              {it.is_primary
                ? <Star weight="fill" className="text-amber-500" size={16} />
                : <Star className="text-slate-400" size={16} />}
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => removeItem(idx)}
              disabled={items.length === 1 && !it.email}
              title="Entfernen"
              data-testid={`contact-email-remove-${idx}`}
              className="h-9 w-9 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            >
              <Trash size={14} />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addRow}
          className="h-7 text-xs gap-1"
          data-testid="contact-email-add"
        >
          <Plus size={12} /> Weitere E-Mail-Adresse
        </Button>
      </div>
    </div>
  );
}
